import { useEffect, useMemo, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { AiSearchRecommendationContext } from '@/components/items/AiSearchRecommendationContext';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader } from '@/components/ui/Card';
import { CodeBlock } from '@/components/items/CodeBlock';
import { buildPreviewSrcDoc, RenderedPreviewFrame } from '@/components/items/RenderedPreviewFrame';
import { ItemThumbnailDropButton } from '@/components/items/ItemThumbnailDropButton';
import { Skeleton } from '@/components/ui/Skeleton';
import { useFavoriteAction } from '@/hooks/useFavorites';
import { useAiSearch, useItem, useItems } from '@/hooks/useItems';
import { DRAWER_SEARCH_PARAM_ITEM_ID } from '@/constants/drawer-search';
import { ROLE_SLUG } from '@/constants/role-policy';
import { buildRelatedItemsSearch, getRelatedItemScore } from '@/lib/item-relatedness';
import { getPublicDisplayTags, getPublicPrimaryRole } from '@/lib/item-public-metadata';
import { buildSiteHref } from '@/lib/site-paths';
import { useToast } from '@/components/ui/Toast';
import type { ItemListItem } from '@/types';
import type { ItemFilterSearch } from '@/lib/validation';
import { useAuthStore } from '@/stores/auth-store';

interface ItemDetailViewProps {
  itemId: number;
  mode: 'page' | 'drawer';
  onClose?: () => void;
  backToSearch?: ItemFilterSearch;
}

const previewViewportConfigs = [
  {
    key: 'desktop',
    label: 'Desktop',
    frameWrapClassName: 'max-w-full',
  },
  {
    key: 'tablet',
    label: 'Tablet',
    frameWrapClassName: 'max-w-[900px]',
  },
  {
    key: 'mobile',
    label: 'Mobile',
    frameWrapClassName: 'max-w-[430px]',
  },
] as const;

type PreviewViewportKey = typeof previewViewportConfigs[number]['key'];

function buildHtmlDownloadFilename(title: string): string {
  const safeTitle = title
    .trim()
    .replace(/[<>:"/\\|?*\u0000-\u001F]/g, '')
    .replace(/\s+/g, '-');

  return `${safeTitle || 'copypaste-item'}.html`;
}

export function ItemDetailView({ itemId, mode, onClose, backToSearch }: ItemDetailViewProps) {
  const { data: item, isLoading, isError, error } = useItem(itemId);
  const { toast } = useToast();
  const user = useAuthStore((state) => state.user);
  const [isCodeExpanded, setIsCodeExpanded] = useState(false);
  const [selectedPreviewViewport, setSelectedPreviewViewport] = useState<PreviewViewportKey>('desktop');
  const codePanelId = 'item-detail-code-panel';
  const favorite = useFavoriteAction(itemId);
  const code = item?.code.trim() ?? '';
  const aiSearchRecommendation = useAiSearch(backToSearch ?? {}, { enabled: Boolean(backToSearch?.query) });
  const downloadableHtml = useMemo(
    () => buildPreviewSrcDoc(item?.title ?? 'CopyPaste', code, item?.tailwindVersion ?? 'none'),
    [code, item?.tailwindVersion, item?.title],
  );
  const relatedFilters = useMemo<ItemFilterSearch>(() => buildRelatedItemsSearch(item), [item]);
  const { data: relatedItemsData } = useItems(relatedFilters, { enabled: Boolean(item) });
  const isRelatedLoading = Boolean(item) && relatedItemsData === undefined;
  const relatedCandidates = item
    ? (relatedItemsData?.pages.flatMap((page) => page.data) ?? [])
    : [];
  const enabledPreviewViewports = previewViewportConfigs.filter((viewport) => item?.previewViewports[viewport.key]);
  const activePreviewViewport = enabledPreviewViewports.find((viewport) => viewport.key === selectedPreviewViewport)
    ?? enabledPreviewViewports[0]
    ?? null;
  const relatedItems = item
    ? relatedCandidates
      .filter((candidate: ItemListItem) => candidate.id !== item.id)
      .map((candidate: ItemListItem) => ({ candidate, score: getRelatedItemScore(item, candidate) }))
      .filter(({ score }) => score > 0)
      .sort((left: { candidate: ItemListItem; score: number }, right: { candidate: ItemListItem; score: number }) => {
        if (right.score !== left.score) {
          return right.score - left.score;
        }

        return left.candidate.title.localeCompare(right.candidate.title);
      })
      .slice(0, 4)
      .map(({ candidate }: { candidate: ItemListItem }) => candidate)
    : [];

  useEffect(() => {
    setIsCodeExpanded(false);
    setSelectedPreviewViewport('desktop');
  }, [itemId]);

  useEffect(() => {
    if (!activePreviewViewport) {
      return;
    }

    if (activePreviewViewport.key !== selectedPreviewViewport) {
      setSelectedPreviewViewport(activePreviewViewport.key);
    }
  }, [activePreviewViewport, selectedPreviewViewport]);

  if (isLoading) {
    return (
      <div className="space-y-4" data-ai-id="item-detail-loading">
        <Skeleton className="h-56 w-full" data-ai-id="item-detail-loading-preview" />
        <Skeleton className="h-8 w-2/3" data-ai-id="item-detail-loading-title" />
        <Skeleton className="h-24 w-full" data-ai-id="item-detail-loading-body" />
      </div>
    );
  }

  if (isError) {
    return (
      <Card data-ai-id="item-detail-error">
        <CardContent className="space-y-3 py-8 text-center" data-ai-id="item-detail-error-content">
          <p className="text-sm font-medium text-rose-600" data-ai-id="item-detail-error-title">
            We couldn't load the item details.
          </p>
          <p className="text-sm text-slate-500" data-ai-id="item-detail-error-message">
            {error instanceof Error ? error.message : 'Unknown error'}
          </p>
          {mode === 'drawer' && onClose ? (
            <Button
              variant="secondary"
              onClick={onClose}
              data-ai-id="item-detail-error-close-button"
              aiIdPrefix="item-detail-error-close-button"
            >
              Close
            </Button>
          ) : null}
        </CardContent>
      </Card>
    );
  }

  if (!item) {
    return (
      <p className="text-sm text-slate-500" data-ai-id="item-detail-empty">
        Item not found.
      </p>
    );
  }

  const buildRelatedItemSearch = (relatedItemId: number) => {
    if (mode === 'drawer') {
      return {
        query: backToSearch?.query,
        type: backToSearch?.type,
        ui: backToSearch?.ui,
        tags: backToSearch?.tags,
        [DRAWER_SEARCH_PARAM_ITEM_ID]: relatedItemId,
      };
    }

    return backToSearch ?? {};
  };

  const shouldShowPreviewCard = enabledPreviewViewports.length > 0;
  const isAdminUser = Boolean(user?.isSuperAdmin || user?.roles.includes(ROLE_SLUG.ADMINISTRATOR));
  const canEditItem = isAdminUser && Boolean(item.adminEditUrl);
  const shouldRenderDrawerActionsInPreviewHeader = mode === 'drawer' && shouldShowPreviewCard && Boolean(code);
  const primaryRole = getPublicPrimaryRole(item);
  const visibleTags = getPublicDisplayTags(item);
  const aiSearchPlan = backToSearch?.query ? aiSearchRecommendation.data?.plan ?? null : null;

  return (
    <div className="space-y-5" data-ai-id="item-detail-view">
      {mode === 'page' ? (
        <div className="space-y-2" data-ai-id="item-detail-page-header">
          <Link
            to="/"
            search={backToSearch}
            className="text-sm font-medium text-slate-500 hover:text-slate-900"
            data-ai-id="item-detail-back-link"
          >
            ← Back to library
          </Link>
          <div className="flex flex-wrap items-center justify-between gap-3" data-ai-id="item-detail-page-title-row">
            <h2 className="text-2xl font-bold text-[#333]" data-ai-id="item-detail-title">
              {item.title}
            </h2>
            <div className="flex flex-wrap items-center gap-2" data-ai-id="item-detail-page-actions">
              {canEditItem ? (
                <a
                  href={item.adminEditUrl ?? undefined}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex h-8 items-center justify-center gap-2 border border-[#ccc] bg-[#f5f5f5] px-3 text-xs font-bold text-[#333] transition-none hover:bg-[#e6e6e6] focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  data-ai-id="item-detail-edit-button"
                >
                  <span data-ai-id="item-detail-edit-button-label">Edit</span>
                </a>
              ) : null}
              <Button
                type="button"
                size="sm"
                variant={favorite.isFavorited ? 'default' : 'secondary'}
                loading={favorite.isLoading}
                disabled={!item}
                onClick={() => {
                  void favorite.toggleFavoriteOrRedirect().catch(() => undefined);
                }}
                data-ai-id="item-detail-favorite-button"
                aiIdPrefix="item-detail-favorite-button"
                aria-pressed={favorite.isFavorited}
              >
                {favorite.isFavorited ? 'Saved' : 'Save'}
              </Button>
              <Button
                type="button"
                size="sm"
                variant="secondary"
                onClick={async () => {
                  try {
                    const shareUrl = new URL(buildSiteHref(`/items/${item.id}`), window.location.origin).toString();
                    await navigator.clipboard.writeText(shareUrl);
                    toast({
                      title: 'Link copied.',
                      description: 'The item URL was copied to your clipboard.',
                      tone: 'success',
                    });
                  } catch {
                    toast({
                      title: "Couldn't copy link.",
                      description: 'Please check browser permissions and try again.',
                      tone: 'error',
                    });
                  }
                }}
                data-ai-id="item-detail-copy-link-button"
                aiIdPrefix="item-detail-copy-link-button"
              >
                Copy link
              </Button>
              <Button
                type="button"
                size="sm"
                variant="secondary"
                className="border-emerald-700 bg-emerald-600 text-white shadow-sm hover:bg-emerald-500 focus:border-emerald-700 focus:ring-emerald-500"
                disabled={!code}
                onClick={async () => {
                  if (!code) {
                    return;
                  }

                  try {
                    await navigator.clipboard.writeText(code);
                    toast({
                      title: 'Code copied.',
                      description: 'Copied to your clipboard.',
                      tone: 'success',
                    });
                  } catch {
                    toast({
                      title: "Couldn't copy.",
                      description: 'Please check browser permissions and try again.',
                      tone: 'error',
                    });
                  }
                }}
                data-ai-id="item-detail-quick-copy-code-button"
                aiIdPrefix="item-detail-quick-copy-code-button"
              >
                Copy code
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      {shouldShowPreviewCard ? (
        code ? (
          <Card data-ai-id="item-detail-preview-card">
            <CardHeader className="overflow-x-auto" data-ai-id="item-detail-preview-header">
              <div className="flex w-full min-w-max items-center justify-between gap-3">
                <div
                  className="flex flex-nowrap gap-2 whitespace-nowrap"
                  role="tablist"
                  aria-label="Preview viewport"
                  data-ai-id="item-detail-preview-tabs"
                >
                  {enabledPreviewViewports.map((viewport) => {
                    const isActive = activePreviewViewport?.key === viewport.key;

                    return (
                      <button
                        key={viewport.key}
                        type="button"
                        role="tab"
                        id={`item-detail-preview-tab-${viewport.key}`}
                        aria-selected={isActive}
                        aria-controls={`item-detail-preview-panel-${viewport.key}`}
                        className="inline-flex h-8 shrink-0 items-center justify-center border px-3 text-xs font-bold transition-none focus:outline-none focus:ring-1 focus:ring-blue-500"
                        style={{
                          borderColor: isActive ? '#0055cc' : '#ccc',
                          backgroundColor: isActive ? '#eaf3ff' : '#fff',
                          color: isActive ? '#0055cc' : '#666',
                        }}
                        onClick={() => setSelectedPreviewViewport(viewport.key)}
                        data-ai-id={`item-detail-preview-tab-${viewport.key}`}
                      >
                        {viewport.label}
                      </button>
                    );
                  })}
                </div>

                {shouldRenderDrawerActionsInPreviewHeader ? (
                  <div className="flex shrink-0 flex-nowrap items-center gap-2 whitespace-nowrap" data-ai-id="item-detail-drawer-actions">
                    {canEditItem ? (
                      <>
                        <ItemThumbnailDropButton itemId={item.id} onSuccess={mode === 'drawer' ? onClose : undefined} />
                        <a
                          href={item.adminEditUrl ?? undefined}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex h-8 shrink-0 items-center justify-center gap-2 border border-[#ccc] bg-[#f5f5f5] px-3 text-xs font-bold text-[#333] transition-none hover:bg-[#e6e6e6] focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                          data-ai-id="item-detail-edit-button"
                        >
                          <span data-ai-id="item-detail-edit-button-label">Edit</span>
                        </a>
                      </>
                    ) : null}
                    <Button
                      type="button"
                      size="sm"
                      variant={favorite.isFavorited ? 'default' : 'secondary'}
                      loading={favorite.isLoading}
                      disabled={!item}
                      onClick={() => {
                        void favorite.toggleFavoriteOrRedirect().catch(() => undefined);
                      }}
                      className="shrink-0"
                      data-ai-id="item-detail-favorite-button"
                      aiIdPrefix="item-detail-favorite-button"
                      aria-pressed={favorite.isFavorited}
                    >
                      {favorite.isFavorited ? 'Saved' : 'Save'}
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      onClick={async () => {
                        try {
                          const shareUrl = new URL(buildSiteHref(`/items/${item.id}`), window.location.origin).toString();
                          await navigator.clipboard.writeText(shareUrl);
                          toast({
                            title: 'Link copied.',
                            description: 'The item URL was copied to your clipboard.',
                            tone: 'success',
                          });
                        } catch {
                          toast({
                            title: "Couldn't copy link.",
                            description: 'Please check browser permissions and try again.',
                            tone: 'error',
                          });
                        }
                      }}
                      className="shrink-0"
                      data-ai-id="item-detail-copy-link-button"
                      aiIdPrefix="item-detail-copy-link-button"
                    >
                      Copy link
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      className="shrink-0 border-emerald-700 bg-emerald-600 text-white shadow-sm hover:bg-emerald-500 focus:border-emerald-700 focus:ring-emerald-500"
                      disabled={!code}
                      onClick={async () => {
                        if (!code) {
                          return;
                        }

                        try {
                          await navigator.clipboard.writeText(code);
                          toast({
                            title: 'Code copied.',
                            description: 'Copied to your clipboard.',
                            tone: 'success',
                          });
                        } catch {
                          toast({
                            title: "Couldn't copy.",
                            description: 'Please check browser permissions and try again.',
                            tone: 'error',
                          });
                        }
                      }}
                      data-ai-id="item-detail-quick-copy-code-button"
                      aiIdPrefix="item-detail-quick-copy-code-button"
                    >
                      Copy code
                    </Button>
                    {onClose ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="shrink-0"
                        onClick={onClose}
                        data-ai-id="item-detail-drawer-close-button"
                        aiIdPrefix="item-detail-drawer-close-button"
                      >
                        Close
                      </Button>
                    ) : null}
                  </div>
                ) : null}
              </div>
            </CardHeader>

            <CardContent data-ai-id="item-detail-preview-grid">
              {activePreviewViewport ? (
                <div
                  role="tabpanel"
                  id={`item-detail-preview-panel-${activePreviewViewport.key}`}
                  aria-labelledby={`item-detail-preview-tab-${activePreviewViewport.key}`}
                  className="overflow-hidden border border-[#ccc] bg-white"
                  data-ai-id="item-detail-preview-viewport"
                >
                  <div className="bg-[#e8e8e8]" data-ai-id="item-detail-preview-viewport-body">
                    <div
                      className={[
                        'mx-auto w-full overflow-hidden border border-slate-300 bg-white',
                        activePreviewViewport.frameWrapClassName,
                      ].join(' ')}
                      data-ai-id="item-detail-preview-viewport-frame-wrap"
                    >
                      <RenderedPreviewFrame
                        title={item.title}
                        titleSuffix={activePreviewViewport.label}
                        source={code}
                        tailwindVersion={item.tailwindVersion}
                        itemType={item.type}
                        style={activePreviewViewport.key === 'desktop' ? { minHeight: '90vh' } : undefined}
                      />
                    </div>
                  </div>
                </div>
              ) : null}
            </CardContent>
          </Card>
        ) : item.previewImageUrl ? (
          <img
            src={item.previewImageUrl}
            alt={item.title}
            width={item.previewImageWidth ?? undefined}
            height={item.previewImageHeight ?? undefined}
            className="h-full w-full object-cover"
            data-ai-id="item-detail-preview-image"
          />
        ) : (
          <div className="flex min-h-[240px] items-center justify-center text-sm text-slate-400" data-ai-id="item-detail-preview-empty">
            No preview code available.
          </div>
        )
      ) : null}

      {backToSearch?.query && aiSearchPlan ? (
        <AiSearchRecommendationContext
          query={backToSearch.query}
          plan={aiSearchPlan}
          item={item}
          variant="detail"
          aiIdPrefix="item-detail-ai-recommendation-context"
        />
      ) : null}

      {isRelatedLoading || relatedItems.length > 0 ? (
        <Card data-ai-id="item-detail-related-card">
          <CardHeader data-ai-id="item-detail-related-header">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500" data-ai-id="item-detail-related-kicker">
              Related
            </p>
            <h4 className="text-sm font-semibold text-slate-900" data-ai-id="item-detail-related-title">
              Related items
            </h4>
          </CardHeader>
          <CardContent data-ai-id="item-detail-related-content">
            {isRelatedLoading ? (
              <div className="grid gap-3 sm:grid-cols-2" data-ai-id="item-detail-related-loading">
                {Array.from({ length: 4 }).map((_, index) => (
                  <div key={index} className="space-y-3 border border-[#ddd] bg-[#f9f9f9] p-3" data-ai-id="item-detail-related-loading-item">
                    <div className="h-16 w-16 bg-[#eee]" data-ai-id="item-detail-related-loading-thumb" />
                    <div className="space-y-2" data-ai-id="item-detail-related-loading-lines">
                      <div className="h-4 w-1/2 bg-slate-200" data-ai-id="item-detail-related-loading-line" />
                      <div className="h-3 w-full bg-slate-200" data-ai-id="item-detail-related-loading-line" />
                      <div className="h-3 w-5/6 bg-slate-200" data-ai-id="item-detail-related-loading-line" />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid gap-3 sm:grid-cols-2" data-ai-id="item-detail-related-list">
                {relatedItems.map((relatedItem) => (
                  <Link
                    key={relatedItem.id}
                    to={mode === 'drawer' ? '/' : '/items/$itemId'}
                    params={mode === 'drawer' ? undefined : { itemId: String(relatedItem.id) }}
                    search={buildRelatedItemSearch(relatedItem.id)}
                    className="group overflow-hidden border border-[#ddd] bg-[#f5f5f5] transition-none hover:border-[#bbb] hover:bg-[#eaf3ff]"
                    data-ai-id="item-detail-related-item"
                  >
                    <div
                      className="aspect-[4/3] w-full overflow-hidden bg-[#eee]"
                      data-ai-id="item-detail-related-item-image"
                    >
                      {relatedItem.previewImageUrl ? (
                        <img
                          src={relatedItem.previewImageUrl}
                          alt={relatedItem.title}
                          width={relatedItem.previewImageWidth ?? undefined}
                          height={relatedItem.previewImageHeight ?? undefined}
                          className="h-full w-full object-cover"
                          data-ai-id="item-detail-related-item-image-element"
                        />
                      ) : null}
                    </div>
                    <div className="space-y-2 px-3 py-3" data-ai-id="item-detail-related-item-copy">
                      <div className="flex flex-wrap items-center gap-2" data-ai-id="item-detail-related-item-badges">
                        {relatedItem.primaryRole ? (
                          <Badge variant="default" data-ai-id="item-detail-related-item-primary-role-badge">
                            {relatedItem.primaryRole.name}
                          </Badge>
                        ) : null}
                        <Badge variant="outline" data-ai-id="item-detail-related-item-type-badge">
                          {relatedItem.type}
                        </Badge>
                      </div>
                      <p className="text-sm font-semibold leading-5 text-slate-900" data-ai-id="item-detail-related-item-title">
                        {relatedItem.title}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      ) : null}

      <Card data-ai-id="item-detail-code-card">
        <CardHeader className="flex flex-row items-center justify-between gap-3" data-ai-id="item-detail-code-header">
          <div data-ai-id="item-detail-code-summary">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500" data-ai-id="item-detail-code-kicker">
              Code
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2" data-ai-id="item-detail-code-actions">
            <Button
              variant="ghost"
              size="sm"
              disabled={!code}
              data-ai-id="item-detail-code-toggle-button"
              aiIdPrefix="item-detail-code-toggle-button"
              aria-expanded={isCodeExpanded}
              aria-controls={codePanelId}
              onClick={() => setIsCodeExpanded((current) => !current)}
            >
              {isCodeExpanded ? 'Collapse code' : 'Expand code'}
            </Button>
            <Button
              variant="secondary"
              size="sm"
              className="border-emerald-700 bg-emerald-600 text-white shadow-sm hover:bg-emerald-500 focus:border-emerald-700 focus:ring-emerald-500"
              disabled={!code}
              data-ai-id="item-detail-copy-button"
              aiIdPrefix="item-detail-copy-button"
              onClick={async () => {
                if (!code) return;

                try {
                  await navigator.clipboard.writeText(code);
                  toast({
                    title: 'Code copied.',
                    description: 'Copied to your clipboard.',
                    tone: 'success',
                  });
                } catch {
                  toast({
                    title: "Couldn't copy.",
                    description: 'Please check browser permissions and try again.',
                    tone: 'error',
                  });
                }
              }}
            >
              Copy
            </Button>
            <Button
              variant="secondary"
              size="sm"
              disabled={!code}
              data-ai-id="item-detail-download-html-button"
              aiIdPrefix="item-detail-download-html-button"
              onClick={() => {
                if (!code) return;

                try {
                  const blob = new Blob([downloadableHtml], {
                    type: 'text/html;charset=utf-8',
                  });
                  const downloadUrl = URL.createObjectURL(blob);
                  const anchor = document.createElement('a');
                  anchor.href = downloadUrl;
                  anchor.download = buildHtmlDownloadFilename(item.title);
                  document.body.append(anchor);
                  anchor.click();
                  anchor.remove();
                  URL.revokeObjectURL(downloadUrl);

                  toast({
                    title: 'HTML downloaded.',
                    description: 'Saved the current preview as an HTML file.',
                    tone: 'success',
                  });
                } catch {
                  toast({
                    title: "Couldn't download HTML.",
                    description: 'Please try again in a moment.',
                    tone: 'error',
                  });
                }
              }}
            >
              Download HTML
            </Button>
          </div>
        </CardHeader>
        <CardContent id={codePanelId} data-ai-id={isCodeExpanded ? 'item-detail-code-content' : 'item-detail-code-preview'}>
          <div
            className={isCodeExpanded ? undefined : 'relative max-h-[10vh] overflow-hidden'}
            data-ai-id={isCodeExpanded ? 'item-detail-code-content-wrap' : 'item-detail-code-preview-wrap'}
          >
            <CodeBlock code={code} language="tailwind" />
            {!isCodeExpanded ? (
              <div
                className="pointer-events-none absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-slate-950 via-slate-950/90 to-transparent"
                aria-hidden="true"
                data-ai-id="item-detail-code-preview-fade"
              />
            ) : null}
          </div>
        </CardContent>
      </Card>

      <Card data-ai-id="item-detail-meta-card">
        <CardContent className="space-y-4 pt-5" data-ai-id="item-detail-meta-content">
          <div className="space-y-3" data-ai-id="item-detail-meta-badges">
            <div className="flex flex-wrap items-center gap-2" data-ai-id="item-detail-meta-summary-badges">
              {primaryRole ? (
                <Badge variant="default" data-ai-id="item-detail-primary-role-badge">
                  {primaryRole.name}
                </Badge>
              ) : null}
              <Badge variant="outline" data-ai-id="item-detail-type-badge">{item.type}</Badge>
              <Badge variant="secondary" data-ai-id="item-detail-tailwind-badge">Tailwind {item.tailwindVersion}</Badge>
              {item.isFeatured ? <Badge variant="secondary" data-ai-id="item-detail-featured-badge">Featured</Badge> : null}
            </div>

            {visibleTags.length > 0 ? (
              <div className="space-y-2" data-ai-id="item-detail-curated-tags">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500" data-ai-id="item-detail-curated-tags-label">
                  Curated tags
                </p>
                <div className="flex flex-wrap gap-2" data-ai-id="item-detail-curated-tag-list">
                  {visibleTags.map((tag) => (
                    <Badge key={tag.slug} variant="outline" data-ai-id="item-detail-curated-tag-badge">
                      {tag.name}
                    </Badge>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
          <p className="text-sm leading-6 text-slate-600" data-ai-id="item-detail-description">
            {item.description || item.excerpt || 'No description available.'}
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
